<!--
  Collapsible block — the `sbi.ui.accordion(summary, body, opts?)`
  primitive. Uses native `<details>` / `<summary>` so accessibility
  (focus, keyboard, SR exposure) is free.

  Body renders eagerly on every pass (Q19 decision) — collapsed state is
  CSS-only via `<details>` toggle. Markers inside collapsed accordions
  still ride the Phase-1 query batch.

  Viewer-toggled open state survives `sbi.state` re-renders (Q13):
  EmbedHost passes a stable `:key` to InsightNode so the native
  `<details>` element survives Vue patches.

  Emits `toggle` upward (`InsightNode` → EmbedHost) which becomes a
  `lucent-accordion-toggle` CustomEvent on `<lucent-insight>`.

  Embedder theming:
    - data-part="accordion" / "accordion-summary" / "accordion-body"
    - --lucent-ui-accordion-bg            — body background
    - --lucent-ui-accordion-border        — outer border
    - --lucent-ui-accordion-summary-bg    — summary bar background
    - --lucent-ui-accordion-summary-fg    — summary text colour
    - --lucent-ui-accordion-chevron-color — disclosure chevron tint
-->

<script setup lang="ts">
import type { ChangeValue } from "../sbi/protocol";
import type { TreeNode } from "../sbi/validate-render-op";
import InsightNode from "./InsightNode.vue";
import LucentIcon from "./LucentIcon.vue";
import { usePrintMode } from "../sbi/print-mode.ts";

const props = defineProps<{
  summary: string;
  defaultOpen?: boolean;
  icon?: string;
  iconPosition?: "leading" | "trailing";
  children: ReadonlyArray<TreeNode>;
  loading?: boolean;
  formats?: Record<string, string>;
}>();

const emit = defineEmits<{
  (e: "click-target", id: string, datum?: Record<string, unknown>, rowIndex?: number): void;
  (e: "change-target", id: string, value: ChangeValue): void;
  /** Forwarded from a descendant modal drilldown's X / Esc / backdrop. */
  (e: "chrome-click-target", id: string): void;
  /**
   * Fired on every `<details>` toggle (native DOM event). The host's
   * EmbedHost surfaces this as a `lucent-accordion-toggle` CustomEvent
   * — integrators can hook it for telemetry.
   */
  (e: "accordion-toggle", summary: string, open: boolean): void;
  /** Forwarded from descendant callouts so EmbedHost can surface the DOM event. */
  (e: "callout-rendered", kind: string, title?: string): void;
}>();

// Native `<details>` `toggle` event handler. `e.target.open` is the
// new state. Forwarded upward via the accordion-toggle emit.
const onToggle = (e: Event): void => {
  const t = e.target as HTMLDetailsElement | null;
  if (!t) return;
  emit("accordion-toggle", props.summary, t.open);
};

const isPrint = usePrintMode();
</script>

<template>
  <!--
    Print mode forces every accordion open: a PDF reader can't click
    the disclosure triangle, so collapsed content is invisible
    content. The CSS in `print-styles.css` additionally hides the
    chevron marker and disables the hover affordance so the printed
    summary reads as a section heading rather than a dead control.
  -->
  <details
    class="accordion"
    data-part="accordion"
    :open="isPrint() ? true : (defaultOpen ?? false)"
    @toggle="onToggle"
  >
    <summary class="accordion-summary" data-part="accordion-summary">
      <span class="accordion-chevron" aria-hidden="true">&#9656;</span>
      <LucentIcon
        v-if="icon && (iconPosition ?? 'leading') === 'leading'"
        :name="icon"
        :size="14"
        class="accordion-summary-icon accordion-summary-icon--leading"
      />
      <span class="accordion-summary-text">{{ summary }}</span>
      <LucentIcon
        v-if="icon && iconPosition === 'trailing'"
        :name="icon"
        :size="14"
        class="accordion-summary-icon accordion-summary-icon--trailing"
      />
    </summary>
    <div class="accordion-body" data-part="accordion-body">
      <InsightNode
        v-for="(child, i) in children"
        :key="i"
        :node="child"
        :loading="loading"
        :formats="formats"
        @click-target="(id, datum, rowIndex) => $emit('click-target', id, datum, rowIndex)"
        @change-target="(id, value) => $emit('change-target', id, value)"
        @chrome-click-target="(id) => $emit('chrome-click-target', id)"
        @accordion-toggle="(summary, open) => $emit('accordion-toggle', summary, open)"
        @callout-rendered="(kind, title) => $emit('callout-rendered', kind, title)"
      />
    </div>
  </details>
</template>

<style scoped>
.accordion {
  border: 1px solid var(--lucent-ui-accordion-border, var(--rule));
  border-radius: var(--radius);
  background: var(--lucent-ui-accordion-bg, var(--card));
  margin: var(--space-2) 0;
  overflow: hidden;
  min-width: 0;
}
.accordion-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  cursor: pointer;
  user-select: none;
  font-weight: 600;
  font-size: 14px;
  background: var(--lucent-ui-accordion-summary-bg, transparent);
  color: var(--lucent-ui-accordion-summary-fg, var(--card-ink));
  list-style: none;
  overflow-wrap: anywhere;
  touch-action: manipulation;
}
@media (pointer: coarse) {
  .accordion-summary {
    min-height: 44px;
    box-sizing: border-box;
  }
}
/* Hide the default ::-webkit-details-marker disclosure triangle so our
   custom chevron renders consistently across engines. */
.accordion-summary::-webkit-details-marker {
  display: none;
}
.accordion-summary:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: -2px;
}
.accordion-chevron {
  display: inline-block;
  font-size: 11px;
  line-height: 1;
  transition: transform 150ms ease;
  color: var(--lucent-ui-accordion-chevron-color, var(--card-muted));
}
.accordion[open] .accordion-chevron {
  transform: rotate(90deg);
}
.accordion-summary-text {
  flex: 1 1 auto;
  min-width: 0;
}
.accordion-summary-icon {
  flex: 0 0 auto;
  color: currentColor;
  opacity: 0.75;
  align-self: center;
}
.accordion-summary-icon--leading {
  margin-right: 4px;
}
.accordion-summary-icon--trailing {
  margin-left: 8px;
}
.accordion-body {
  padding: 12px 14px 14px;
  border-top: 1px solid var(--lucent-ui-accordion-border, var(--rule));
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  /* Container slot: nested components' @container queries measure
     this cell, not the page. Safe because the slot is stretched by
     its parent, never sized to its content. */
  container-type: inline-size;
}
</style>
